import { IconAlertTriangle, IconCheck } from "@tabler/icons-react";
import { Alert, Card, Stack, Text } from "@/components/ui";
import type { DiferenciaProrrateo } from "@/features/prorrateo/types";
import { useFormatoImporte } from "./ProrrateoSociedadContext";

/**
 * Banner superior del prorrateo · muestra subtotal vs suma de líneas vs
 * diferencia. Cuando la suma cuadra (±$0.01) se permite confirmar.
 *
 * Componente 100% presentacional · recibe el VO `DiferenciaProrrateo`
 * calculado upstream por `calcularDiferencia()`.
 */
export function SumaResumenBanner({ diferencia }: { diferencia: DiferenciaProrrateo }) {
  const { suma, subtotal, monto, coincide } = diferencia;
  // Tono de marca (naranja) cuando la suma cuadra · rojo cuando no. La
  // confirmación de éxito NO usa el verde de `Alert` (chocaba con el naranja):
  // es una línea sobria en el mismo naranja del marco y del valor de diferencia.
  const tono = coincide ? "multiva" : "peligro";
  return (
    <Card
      padding="lg"
      style={{
        borderColor: `var(--color-${tono}-400)`,
        background: `color-mix(in srgb, var(--color-${tono}-600) 5%, transparent)`,
      }}
    >
      <Stack gap="sm">
        <div className="grid grid-cols-3 gap-4">
          <SumaCell label="Subtotal del CFDI" valor={subtotal} />
          <SumaCell label="Suma del prorrateo" valor={suma} />
          <SumaCell label="Diferencia" valor={monto} color={`${tono}-600`} signo />
        </div>
        {coincide ? (
          <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "light-dark(var(--color-multiva-700), var(--color-multiva-300))" }}>
            <IconCheck size={16} stroke={1.9} className="shrink-0" />
            La suma del prorrateo coincide con el subtotal del CFDI · puedes confirmar.
          </div>
        ) : (
          <Alert variant="error" icon={<IconAlertTriangle size={16} stroke={1.7} />}>
            La suma del prorrateo <strong>no coincide</strong> con el subtotal del CFDI (tolerancia ±$0.01). Ajusta los importes antes de confirmar.
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

function SumaCell({ label, valor, color, signo }: { label: string; valor: number; color?: string; signo?: boolean }) {
  const fmt = useFormatoImporte();
  const prefix = signo && valor >= 0 ? "+" : "";
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="text-[10px] tracking-wide">
        {label}
      </Text>
      <Text size="lg" fw={600} c={color}>
        {prefix}{fmt(valor)}
      </Text>
    </div>
  );
}
